import { useCallback, useEffect, useMemo, useState, type CSSProperties } from "react";
import { APP_CURRENCY, APP_LOCALE } from "../../../config/locale";
import { Modal } from "../../../components/Modal";
import {
  SearchableSelect,
  type SearchableSelectOption,
} from "../../../components/SearchableSelect";
import { NovaLinhaSubempreitadaModal } from "./NovaLinhaSubempreitadaModal";
import type { Subcontractor } from "../../../subempreiteiros/subempreiteirosApi";
import type { Supplier } from "../../../fornecedores/fornecedoresApi";
import {
  getWorkSubcontractors,
  type WorkSubcontractorItem,
} from "../../../obras/obrasApi";
import {
  ApiError,
  approveSubcontractorAuto,
  createSubcontractorAuto,
  deleteSubcontractorAuto,
  getSubcontractorAutos,
  getSubcontractorRetentions,
  registerSubcontractorInvoice,
  registerSubcontractorPayment,
  reopenSubcontractorAuto,
  updateSubcontractorAuto,
  type SubcontractorAutoDto,
  type SubcontractorAutoStatus,
  type SubcontractorRetentionsDto,
} from "../../../subcontractorAutos/subcontractorAutosApi";

interface WorkSubcontractorAutosTabProps {
  workId: number;
  // Para o atalho de criar a linha em falta sem sair deste separador.
  subcontractors: Subcontractor[];
  suppliers: Supplier[];
  /**
   * A linha criada pelo atalho tem de subir ao pai. Este separador tem a sua própria cópia de
   * `items`, mas a lista que alimenta o separador Sub-empreiteiros e os totais da obra vive no
   * EditarObraView e só é carregada uma vez, por obra — recarregar aqui deixava-a desatualizada
   * até alguém recarregar a página.
   */
  onLineCreated?: (item: WorkSubcontractorItem) => void;
}

const currencyFormatter = new Intl.NumberFormat(APP_LOCALE, {
  style: "currency",
  currency: APP_CURRENCY,
});

const formatCurrency = (value: number | null | undefined): string => {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return currencyFormatter.format(0);
  }
  return currencyFormatter.format(value);
};

const formatDate = (value?: string | null): string => {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "—";
  return parsed.toLocaleDateString(APP_LOCALE);
};

const todayInput = (): string => new Date().toISOString().slice(0, 10);

// Parse tolerante (aceita vírgula ou ponto), como nos restantes tabs da obra.
const parseNumber = (value: string): number => {
  const normalized = value.trim().replace(/\s/g, "").replace(",", ".");
  if (!normalized) return 0;
  const parsed = Number.parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
};

const statusLabel = (status: SubcontractorAutoStatus): string => {
  switch (status) {
    case "approved":
      return "Aprovado";
    case "invoiced":
      return "Faturado";
    case "paid":
      return "Pago";
    default:
      return "Rascunho";
  }
};

// Badge de estado. Verde tingido (aprovado) usa border-border porque
// border-green-500/20 não existe no Tailwind pré-compilado; "paid" (terminal)
// usa verde sólido via token inline para se distinguir do "aprovado".
const statusBadgeClass = (status: SubcontractorAutoStatus): string => {
  if (status === "invoiced") return "bg-primary/10 text-primary border border-primary/20";
  if (status === "approved") return "bg-green-500/10 text-green-700 border border-border";
  if (status === "paid") return "text-white border border-border";
  return "bg-muted text-secondary-text border border-border";
};

const statusBadgeStyle = (status: SubcontractorAutoStatus): CSSProperties => {
  const base: CSSProperties = { borderRadius: "var(--radius)" };
  if (status === "paid") return { ...base, backgroundColor: "var(--color-green-600)" };
  return base;
};

type LifecycleAction = "approve" | "reopen";

interface CreateForm {
  workSubcontractorItemId: string;
  measuredAmount: string;
  retentionPercent: string;
  notes: string;
}

interface EditForm {
  measuredAmount: string;
  retentionPercent: string;
  notes: string;
}

interface InvoiceForm {
  invoiceNumber: string;
  invoiceDate: string;
  invoiceAmount: string;
}

interface PaymentForm {
  amount: string;
  paidAt: string;
}

type ModalState =
  | { kind: "create"; form: CreateForm }
  | { kind: "edit"; auto: SubcontractorAutoDto; form: EditForm }
  | { kind: "invoice"; auto: SubcontractorAutoDto; form: InvoiceForm }
  | { kind: "payment"; auto: SubcontractorAutoDto; form: PaymentForm }
  | null;

const inputClass =
  "px-3 py-2 border border-border rounded bg-background text-foreground text-sm";
const linkAction =
  "underline cursor-pointer transition-opacity hover:opacity-70 disabled:opacity-50 disabled:cursor-not-allowed";

export function WorkSubcontractorAutosTab({
  workId,
  subcontractors,
  suppliers,
  onLineCreated,
}: WorkSubcontractorAutosTabProps) {
  const [isCreatingLine, setIsCreatingLine] = useState(false);
  const [autos, setAutos] = useState<SubcontractorAutoDto[]>([]);
  const [retentions, setRetentions] = useState<SubcontractorRetentionsDto | null>(null);
  const [items, setItems] = useState<WorkSubcontractorItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [actioning, setActioning] = useState<{ id: number; kind: LifecycleAction } | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);

  const [modal, setModal] = useState<ModalState>(null);
  const [isSaving, setIsSaving] = useState(false);

  const load = useCallback(
    async (silent = false) => {
      if (!silent) setIsLoading(true);
      setError(null);
      try {
        const [autosData, retentionsData, itemsData] = await Promise.all([
          getSubcontractorAutos(workId),
          getSubcontractorRetentions(workId),
          getWorkSubcontractors(workId),
        ]);
        setAutos(autosData);
        setRetentions(retentionsData);
        setItems(itemsData);
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : "Não foi possível carregar os autos de subempreiteiro.",
        );
      } finally {
        if (!silent) setIsLoading(false);
      }
    },
    [workId],
  );

  useEffect(() => {
    load();
  }, [load]);

  const total = useMemo(
    () =>
      autos.reduce(
        (sum, auto) => sum + (Number.isFinite(auto.netPayable) ? auto.netPayable : 0),
        0,
      ),
    [autos],
  );

  // O código entra na etiqueta porque é ela que fica visível depois de escolher: sem
  // ele, quem escolheu deixava de ver de que linha se tratava. O subempreiteiro e o
  // valor vão para a segunda linha, que só se lê com a lista aberta.
  const itemOptions = useMemo<SearchableSelectOption<string>[]>(
    () =>
      items.map((item) => ({
        value: String(item.id),
        label: item.code
          ? `${item.code} · ${item.description}`
          : item.description,
        description: `${item.subcontractor} · ${formatCurrency(item.value)}`,
        keywords: item.code,
      })),
    [items],
  );

  // ---- Abertura de modais ---------------------------------------------------
  const openCreate = () => {
    setError(null);
    setModal({
      kind: "create",
      form: { workSubcontractorItemId: "", measuredAmount: "", retentionPercent: "5", notes: "" },
    });
  };

  const openEdit = (auto: SubcontractorAutoDto) => {
    setError(null);
    setModal({
      kind: "edit",
      auto,
      form: {
        measuredAmount: String(auto.measuredAmount ?? ""),
        retentionPercent: String(auto.retentionPercent ?? ""),
        notes: auto.notes ?? "",
      },
    });
  };

  const openInvoice = (auto: SubcontractorAutoDto) => {
    setError(null);
    setModal({
      kind: "invoice",
      auto,
      form: {
        invoiceNumber: auto.invoiceNumber ?? "",
        invoiceDate: todayInput(),
        invoiceAmount: String(auto.measuredAmount ?? ""),
      },
    });
  };

  const openPayment = (auto: SubcontractorAutoDto) => {
    setError(null);
    const outstanding = (auto.netPayable ?? 0) - (auto.paidAmount ?? 0);
    setModal({
      kind: "payment",
      auto,
      form: {
        amount: String(outstanding > 0 ? outstanding : 0),
        paidAt: todayInput(),
      },
    });
  };

  const closeModal = () => setModal(null);

  // ---- Patches de formulário (por-tipo, com narrowing) ----------------------
  const patchCreate = (patch: Partial<CreateForm>) =>
    setModal((prev) =>
      prev && prev.kind === "create" ? { ...prev, form: { ...prev.form, ...patch } } : prev,
    );
  const patchEdit = (patch: Partial<EditForm>) =>
    setModal((prev) =>
      prev && prev.kind === "edit" ? { ...prev, form: { ...prev.form, ...patch } } : prev,
    );
  const patchInvoice = (patch: Partial<InvoiceForm>) =>
    setModal((prev) =>
      prev && prev.kind === "invoice" ? { ...prev, form: { ...prev.form, ...patch } } : prev,
    );
  const patchPayment = (patch: Partial<PaymentForm>) =>
    setModal((prev) =>
      prev && prev.kind === "payment" ? { ...prev, form: { ...prev.form, ...patch } } : prev,
    );

  // Escolher item de subempreitada: pré-preenche o medido com o valor da linha.
  const onSelectItem = (value: string) => {
    const item = items.find((it) => String(it.id) === value);
    patchCreate({
      workSubcontractorItemId: value,
      measuredAmount: item ? String(item.value ?? "") : "",
    });
  };

  // ---- Submissões -----------------------------------------------------------
  const handleCreate = async () => {
    if (!modal || modal.kind !== "create" || isSaving) return;
    const { form } = modal;
    if (!form.workSubcontractorItemId) {
      setError("Escolha uma linha de subempreitada para criar o auto.");
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      await createSubcontractorAuto(workId, {
        workSubcontractorItemId: Number(form.workSubcontractorItemId),
        measuredAmount: parseNumber(form.measuredAmount),
        retentionPercent: parseNumber(form.retentionPercent),
        notes: form.notes.trim() || null,
      });
      closeModal();
      await load(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Não foi possível criar o auto.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!modal || modal.kind !== "edit" || isSaving) return;
    const { auto, form } = modal;
    setIsSaving(true);
    setError(null);
    try {
      await updateSubcontractorAuto(workId, auto.id, {
        measuredAmount: parseNumber(form.measuredAmount),
        retentionPercent: parseNumber(form.retentionPercent),
        notes: form.notes.trim() || null,
      });
      closeModal();
      await load(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Não foi possível guardar o auto.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleInvoice = async () => {
    if (!modal || modal.kind !== "invoice" || isSaving) return;
    const { auto, form } = modal;
    if (!form.invoiceNumber.trim()) {
      setError("Indique o número da fatura.");
      return;
    }
    if (!form.invoiceDate) {
      setError("Indique a data da fatura.");
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      await registerSubcontractorInvoice(workId, auto.id, {
        invoiceNumber: form.invoiceNumber.trim(),
        invoiceDate: form.invoiceDate,
        invoiceAmount: parseNumber(form.invoiceAmount),
      });
      closeModal();
      await load(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Não foi possível registar a fatura.");
    } finally {
      setIsSaving(false);
    }
  };

  const handlePayment = async () => {
    if (!modal || modal.kind !== "payment" || isSaving) return;
    const { auto, form } = modal;
    setIsSaving(true);
    setError(null);
    try {
      await registerSubcontractorPayment(workId, auto.id, {
        amount: parseNumber(form.amount),
        paidAt: form.paidAt || null,
      });
      closeModal();
      await load(true);
    } catch (err) {
      // O backend bloqueia pagamento sem auto aprovado e acima do líquido —
      // a mensagem do ProblemDetails chega humanizada pelo ApiError.
      setError(err instanceof Error ? err.message : "Não foi possível registar o pagamento.");
    } finally {
      setIsSaving(false);
    }
  };

  const runLifecycle = async (autoId: number, kind: LifecycleAction) => {
    if (actioning !== null) return;
    setActioning({ id: autoId, kind });
    setError(null);
    try {
      if (kind === "approve") {
        await approveSubcontractorAuto(workId, autoId);
      } else {
        await reopenSubcontractorAuto(workId, autoId);
      }
      await load(true);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : kind === "approve"
            ? "Não foi possível aprovar o auto."
            : "Não foi possível reabrir o auto.",
      );
    } finally {
      setActioning(null);
    }
  };

  const handleDelete = async (autoId: number) => {
    if (deletingId !== null) return;
    setDeletingId(autoId);
    setError(null);
    try {
      await deleteSubcontractorAuto(workId, autoId);
      setConfirmDeleteId(null);
      await load(true);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setConfirmDeleteId(null);
        await load(true);
      } else {
        setError(err instanceof Error ? err.message : "Não foi possível eliminar o auto.");
      }
    } finally {
      setDeletingId(null);
    }
  };

  // ---- Preview de retenção/líquido (modais criar/editar) --------------------
  const previewOf = (measured: string, percent: string) => {
    const measuredValue = parseNumber(measured);
    const retention = (measuredValue * parseNumber(percent)) / 100;
    return { retention, net: measuredValue - retention };
  };

  return (
    <div className="space-y-6">
      {error && (
        <div
          className="border border-border bg-destructive/10 text-destructive px-4 py-3"
          style={{ borderRadius: "var(--radius)" }}
        >
          {error}
        </div>
      )}

      {/* Painel de retenções de garantia acumuladas */}
      <div
        className="bg-card border border-border px-4 py-4"
        style={{ borderRadius: "var(--radius-card)" }}
      >
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <span className="text-secondary-text text-sm">Retenções de garantia acumuladas</span>
          <span className="text-foreground text-lg font-medium">
            {formatCurrency(retentions?.totalRetention ?? 0)}
          </span>
        </div>
        {retentions && retentions.bySubcontractor.length > 0 ? (
          <ul className="mt-3 space-y-1">
            {retentions.bySubcontractor.map((row) => (
              <li
                key={row.subcontractorName}
                className="flex items-center justify-between gap-3 text-sm"
              >
                <span className="text-foreground break-words">{row.subcontractorName}</span>
                <span className="text-secondary-text whitespace-nowrap">
                  {formatCurrency(row.totalRetention)} · {row.autoCount} auto(s)
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <div className="mt-2 text-sm text-secondary-text">Sem retenções acumuladas.</div>
        )}
      </div>

      {/* Um auto mede sempre contra uma linha de subempreitada contratada. Sem linhas não há nada
          que medir — e até aqui isso manifestava-se como um seletor "Escolher linha…" vazio, sem
          dizer o que faltava nem onde se fazia. Como as linhas só nasciam do orçamento, numa obra
          que não veio de um orçamento com subempreiteiros o auto era simplesmente inalcançável. */}
      {!isLoading && items.length === 0 && (
        <div
          className="border border-border bg-card px-4 py-4"
          style={{ borderRadius: "var(--radius-card)" }}
        >
          <p className="text-sm text-secondary-text">
            Esta obra ainda não tem linhas de subempreitada. O auto mede o que foi executado
            contra uma linha contratada, por isso é preciso criar a linha primeiro.
          </p>
          <button
            type="button"
            onClick={() => setIsCreatingLine(true)}
            className="mt-3 inline-flex items-center justify-center px-4 py-2 min-h-[44px] bg-primary text-primary-foreground cursor-pointer text-sm"
            style={{ borderRadius: "var(--radius-button)" }}
          >
            + Nova linha de subempreitada
          </button>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <span className="text-secondary-text text-sm">
          {autos.length} auto(s) · Líquido total {formatCurrency(total)}
        </span>
        <button
          type="button"
          onClick={openCreate}
          disabled={items.length === 0}
          title={
            items.length === 0
              ? "Crie primeiro uma linha de subempreitada nesta obra."
              : undefined
          }
          className="inline-flex items-center justify-center px-4 py-2 min-h-[44px] bg-primary text-primary-foreground cursor-pointer text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ borderRadius: "var(--radius-button)" }}
        >
          + Novo auto
        </button>
      </div>

      {/* Tabela */}
      <div
        className="bg-card border border-border overflow-x-auto"
        style={{ borderRadius: "var(--radius-card)" }}
      >
        <table className="w-full" style={{ minWidth: 1040 }}>
          <thead>
            <tr className="border-b border-border bg-muted text-left">
              <th className="px-4 py-3" style={{ width: 120 }}>Nº</th>
              <th className="px-4 py-3" style={{ width: 180 }}>Subempreiteiro</th>
              <th className="px-4 py-3" style={{ width: 110 }}>Estado</th>
              <th className="px-4 py-3 text-right" style={{ width: 120 }}>Medido</th>
              <th className="px-4 py-3 text-right" style={{ width: 150 }}>Retenção</th>
              <th className="px-4 py-3 text-right" style={{ width: 130 }}>Líquido a pagar</th>
              <th className="px-4 py-3 text-right" style={{ width: 110 }}>Pago</th>
              <th className="px-4 py-3" style={{ width: 260 }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={8} className="px-4 py-6 text-center text-secondary-text">
                  A carregar…
                </td>
              </tr>
            ) : autos.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-6 text-center text-secondary-text">
                  Sem autos de subempreiteiro. Crie o primeiro auto acima.
                </td>
              </tr>
            ) : (
              autos.map((auto) => (
                <tr key={auto.id} className="border-b border-border last:border-b-0">
                  <td className="px-4 py-3 break-words">{auto.number || "—"}</td>
                  <td className="px-4 py-3 break-words">{auto.subcontractorName || "—"}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 text-xs ${statusBadgeClass(auto.status)}`}
                      style={statusBadgeStyle(auto.status)}
                    >
                      {statusLabel(auto.status)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {formatCurrency(auto.measuredAmount)}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {auto.retentionPercent}% · {formatCurrency(auto.retentionAmount)}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap font-medium">
                    {formatCurrency(auto.netPayable)}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {formatCurrency(auto.paidAmount)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-3 flex-wrap items-center">
                      {auto.status === "draft" && (
                        <button
                          type="button"
                          className={`${linkAction} text-primary`}
                          disabled={actioning?.id === auto.id}
                          onClick={() => runLifecycle(auto.id, "approve")}
                        >
                          {actioning?.id === auto.id && actioning.kind === "approve"
                            ? "A aprovar…"
                            : "Aprovar"}
                        </button>
                      )}
                      {auto.status === "draft" && (
                        <button
                          type="button"
                          className={`${linkAction} text-primary`}
                          onClick={() => openEdit(auto)}
                        >
                          Editar
                        </button>
                      )}
                      {auto.status === "approved" && (
                        <button
                          type="button"
                          className={`${linkAction} text-primary`}
                          disabled={actioning?.id === auto.id}
                          onClick={() => runLifecycle(auto.id, "reopen")}
                        >
                          {actioning?.id === auto.id && actioning.kind === "reopen"
                            ? "A reabrir…"
                            : "Reabrir"}
                        </button>
                      )}
                      {auto.status === "approved" && (
                        <button
                          type="button"
                          className={`${linkAction} text-primary`}
                          onClick={() => openInvoice(auto)}
                        >
                          Registar fatura
                        </button>
                      )}
                      {(auto.status === "approved" || auto.status === "invoiced") && (
                        <button
                          type="button"
                          className={`${linkAction} text-primary`}
                          onClick={() => openPayment(auto)}
                        >
                          Registar pagamento
                        </button>
                      )}
                      {auto.status === "invoiced" && auto.invoiceNumber && (
                        <span className="text-xs text-secondary-text whitespace-nowrap">
                          Fatura {auto.invoiceNumber}
                        </span>
                      )}
                      {auto.status === "paid" && (
                        <span className="text-xs text-green-700 whitespace-nowrap">
                          Pago em {formatDate(auto.paidAt)}
                        </span>
                      )}
                      {auto.status === "draft" &&
                        (confirmDeleteId === auto.id ? (
                          <span className="inline-flex items-center gap-2 flex-wrap">
                            <span className="text-xs text-secondary-text">Apagar?</span>
                            <button
                              type="button"
                              disabled={deletingId === auto.id}
                              onClick={() => handleDelete(auto.id)}
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-destructive/10 text-destructive cursor-pointer disabled:opacity-50"
                              style={{ borderRadius: "var(--radius)" }}
                            >
                              {deletingId === auto.id ? "A eliminar…" : "Confirmar"}
                            </button>
                            <button
                              type="button"
                              disabled={deletingId === auto.id}
                              onClick={() => setConfirmDeleteId(null)}
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-border text-foreground hover:bg-accent/50 cursor-pointer disabled:opacity-50"
                              style={{ borderRadius: "var(--radius)" }}
                            >
                              Cancelar
                            </button>
                          </span>
                        ) : (
                          <button
                            type="button"
                            className={`${linkAction} text-destructive`}
                            onClick={() => setConfirmDeleteId(auto.id)}
                          >
                            Eliminar
                          </button>
                        ))}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {isCreatingLine && (
        <NovaLinhaSubempreitadaModal
          workId={workId}
          subcontractors={subcontractors}
          suppliers={suppliers}
          onClose={() => setIsCreatingLine(false)}
          onCreated={(item) => {
            // Sobe ao pai, senão o separador Sub-empreiteiros e os totais da obra ficavam com a
            // lista anterior — são alimentados por um estado que só carrega uma vez, por obra.
            onLineCreated?.(item);
            // E recarrega em silêncio aqui: a linha nova tem de entrar no seletor "Escolher
            // linha…" para o auto poder ser criado logo a seguir, que é o objectivo do atalho.
            load(true);
          }}
        />
      )}

      {/* Modal — Novo auto */}
      {modal?.kind === "create" && (
        <Modal
          title="Novo auto de subempreiteiro"
          onClose={closeModal}
          maxWidthClass="max-w-2xl"
          footer={
            <>
              <button type="button" onClick={closeModal} className="px-4 py-2 border border-border">
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleCreate}
                disabled={isSaving}
                className="px-4 py-2 bg-primary text-primary-foreground disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving ? "A criar…" : "Criar auto"}
              </button>
            </>
          }
        >
          <div className="space-y-3">
            <SearchableSelect<string>
              label="Linha de subempreitada"
              required
              labelClassName="text-sm text-secondary-text"
              labelStyle={{}}
              value={
                modal.form.workSubcontractorItemId === ""
                  ? null
                  : modal.form.workSubcontractorItemId
              }
              onChange={(next) => onSelectItem(next ?? "")}
              options={itemOptions}
              placeholder="Escolher linha…"
              searchPlaceholder="Procurar linha…"
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="flex flex-col gap-1 text-sm text-secondary-text">
                Valor medido
                <input
                  type="text"
                  inputMode="decimal"
                  value={modal.form.measuredAmount}
                  onChange={(event) => patchCreate({ measuredAmount: event.target.value })}
                  placeholder="0,00"
                  className={inputClass}
                />
              </label>
              <label className="flex flex-col gap-1 text-sm text-secondary-text">
                Retenção (%)
                <input
                  type="text"
                  inputMode="decimal"
                  value={modal.form.retentionPercent}
                  onChange={(event) => patchCreate({ retentionPercent: event.target.value })}
                  placeholder="5"
                  className={inputClass}
                />
              </label>
            </div>
            <label className="flex flex-col gap-1 text-sm text-secondary-text">
              Notas
              <textarea
                rows={2}
                value={modal.form.notes}
                onChange={(event) => patchCreate({ notes: event.target.value })}
                className={inputClass}
              />
            </label>
            <PreviewBox
              {...previewOf(modal.form.measuredAmount, modal.form.retentionPercent)}
            />
          </div>
        </Modal>
      )}

      {/* Modal — Editar auto */}
      {modal?.kind === "edit" && (
        <Modal
          title={`Editar auto ${modal.auto.number || ""}`.trim()}
          onClose={closeModal}
          maxWidthClass="max-w-2xl"
          footer={
            <>
              <button type="button" onClick={closeModal} className="px-4 py-2 border border-border">
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleUpdate}
                disabled={isSaving}
                className="px-4 py-2 bg-primary text-primary-foreground disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving ? "A guardar…" : "Guardar"}
              </button>
            </>
          }
        >
          <div className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="flex flex-col gap-1 text-sm text-secondary-text">
                Valor medido
                <input
                  type="text"
                  inputMode="decimal"
                  value={modal.form.measuredAmount}
                  onChange={(event) => patchEdit({ measuredAmount: event.target.value })}
                  placeholder="0,00"
                  className={inputClass}
                />
              </label>
              <label className="flex flex-col gap-1 text-sm text-secondary-text">
                Retenção (%)
                <input
                  type="text"
                  inputMode="decimal"
                  value={modal.form.retentionPercent}
                  onChange={(event) => patchEdit({ retentionPercent: event.target.value })}
                  placeholder="5"
                  className={inputClass}
                />
              </label>
            </div>
            <label className="flex flex-col gap-1 text-sm text-secondary-text">
              Notas
              <textarea
                rows={2}
                value={modal.form.notes}
                onChange={(event) => patchEdit({ notes: event.target.value })}
                className={inputClass}
              />
            </label>
            <PreviewBox {...previewOf(modal.form.measuredAmount, modal.form.retentionPercent)} />
          </div>
        </Modal>
      )}

      {/* Modal — Registar fatura */}
      {modal?.kind === "invoice" && (
        <Modal
          title="Registar fatura do subempreiteiro"
          onClose={closeModal}
          maxWidthClass="max-w-md"
          footer={
            <>
              <button type="button" onClick={closeModal} className="px-4 py-2 border border-border">
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleInvoice}
                disabled={isSaving}
                className="px-4 py-2 bg-primary text-primary-foreground disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving ? "A registar…" : "Registar fatura"}
              </button>
            </>
          }
        >
          <div className="space-y-3">
            <label className="flex flex-col gap-1 text-sm text-secondary-text">
              Nº da fatura *
              <input
                type="text"
                value={modal.form.invoiceNumber}
                onChange={(event) => patchInvoice({ invoiceNumber: event.target.value })}
                placeholder="FS 2026/..."
                className={inputClass}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-secondary-text">
              Data da fatura *
              <input
                type="date"
                value={modal.form.invoiceDate}
                onChange={(event) => patchInvoice({ invoiceDate: event.target.value })}
                className={inputClass}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-secondary-text">
              Valor da fatura
              <input
                type="text"
                inputMode="decimal"
                value={modal.form.invoiceAmount}
                onChange={(event) => patchInvoice({ invoiceAmount: event.target.value })}
                placeholder="0,00"
                className={inputClass}
              />
            </label>
          </div>
        </Modal>
      )}

      {/* Modal — Registar pagamento */}
      {modal?.kind === "payment" && (
        <Modal
          title="Registar pagamento ao subempreiteiro"
          onClose={closeModal}
          maxWidthClass="max-w-md"
          footer={
            <>
              <button type="button" onClick={closeModal} className="px-4 py-2 border border-border">
                Cancelar
              </button>
              <button
                type="button"
                onClick={handlePayment}
                disabled={isSaving}
                className="px-4 py-2 bg-primary text-primary-foreground disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving ? "A registar…" : "Registar pagamento"}
              </button>
            </>
          }
        >
          <div className="space-y-3">
            <div className="text-sm text-secondary-text">
              Líquido a pagar: {formatCurrency(modal.auto.netPayable)} · Já pago:{" "}
              {formatCurrency(modal.auto.paidAmount)}
            </div>
            <label className="flex flex-col gap-1 text-sm text-secondary-text">
              Valor a pagar
              <input
                type="text"
                inputMode="decimal"
                value={modal.form.amount}
                onChange={(event) => patchPayment({ amount: event.target.value })}
                placeholder="0,00"
                className={inputClass}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-secondary-text">
              Data do pagamento
              <input
                type="date"
                value={modal.form.paidAt}
                onChange={(event) => patchPayment({ paidAt: event.target.value })}
                className={inputClass}
              />
            </label>
          </div>
        </Modal>
      )}
    </div>
  );
}

// Preview ao vivo de retenção e líquido nos modais de criação/edição.
function PreviewBox({ retention, net }: { retention: number; net: number }) {
  return (
    <div
      className="bg-muted border border-border px-3 py-2 text-sm flex items-center justify-between gap-3 flex-wrap"
      style={{ borderRadius: "var(--radius)" }}
    >
      <span className="text-secondary-text">
        Retenção: <span className="text-foreground">{formatCurrency(retention)}</span>
      </span>
      <span className="text-secondary-text">
        Líquido a pagar: <span className="text-foreground font-medium">{formatCurrency(net)}</span>
      </span>
    </div>
  );
}
